/**
 * Debounce window (ms) for refetching `GET /api/notifications/count` from
 * high-frequency triggers (Pusher event bursts, tab visibility changes).
 */
export const NOTIFICATION_COUNT_REFETCH_DEBOUNCE_MS = 500;

/**
 * Minimum interval (ms) between passive `GET /api/notifications/count` refetches
 * (tab-visibility regains, per-navigation triggers). A refetch requested inside
 * this window is skipped when the count is already fresh — unless an invalidating
 * Pusher event forces a refresh. Prevents the once-per-navigation refetch storm
 * seen in production while still reflecting genuine notification changes promptly.
 */
export const NOTIFICATION_COUNT_MIN_INTERVAL_MS = 30_000;

/**
 * Minimum spacing (ms) between FORCED `GET /api/notifications/count` refetches. A burst of live
 * events (Staging Tools adding 50 To do items raises one every few seconds) used to refetch once
 * per event, because each event landed after the 500 ms debounce had already fired; with this
 * spacing a burst reads the count at most once per window, and once more after the last event so
 * the badge still ends fresh.
 */
export const NOTIFICATION_COUNT_FORCED_MIN_INTERVAL_MS = 5_000;

/**
 * Minimum spacing (ms) between bumps of the live Work Queue item counter
 * (`profile.liveNotificationEvents`). Every To do / With Landon / Recommendations / text-thread
 * read keys off that counter, so one bump per item in a burst multiplied into a request storm the
 * API's per-user limit answered with 429s. The first event bumps at once; the rest of a burst
 * collapse into one trailing bump at the end of each window, so the lists still end fresh.
 *
 * 15 s, not 5 s (review B13-M1): staging raises Add 50 To do items about 3 s apart over 60-150 s,
 * so with any window there is an event in every window. A 5 s window still bumped ~30 times for 50
 * events (each bump re-reads five lists and counts); 15 s holds it to ~11, at most one per window
 * whatever the spacing, and the last event is read at most 15 s late.
 */
export const LIVE_EVENT_MIN_INTERVAL_MS = 15_000;

export const SERVICE_REQUEST_NEEDS_TRIAGE_ASSISTANCE_NOTIFICATION =
  'service_requests.needs_triage_assistance';

export const SERVICE_REQUEST_NEEDS_TRIAGE_ASSISTANCE_EVENT = `notification.${SERVICE_REQUEST_NEEDS_TRIAGE_ASSISTANCE_NOTIFICATION}`;

export const SERVICE_REQUEST_NEEDS_TRIAGE_ASSISTANCE_CLEARED_EVENT = `${SERVICE_REQUEST_NEEDS_TRIAGE_ASSISTANCE_EVENT}.cleared`;

export const SERVICE_REQUEST_OPERATIONAL_EXCEPTION_NOTIFICATION =
  'service_requests.operational_exception';

export const SERVICE_REQUEST_OPERATIONAL_EXCEPTION_EVENT = `notification.${SERVICE_REQUEST_OPERATIONAL_EXCEPTION_NOTIFICATION}`;

export const SERVICE_REQUEST_OPERATIONAL_EXCEPTION_CLEARED_EVENT = `${SERVICE_REQUEST_OPERATIONAL_EXCEPTION_EVENT}.cleared`;

export const WORK_ORDER_OPERATIONAL_EXCEPTION_NOTIFICATION = 'work_orders.operational_exception';

export const WORK_ORDER_OPERATIONAL_EXCEPTION_EVENT = `notification.${WORK_ORDER_OPERATIONAL_EXCEPTION_NOTIFICATION}`;

export const WORK_ORDER_OPERATIONAL_EXCEPTION_CLEARED_EVENT = `${WORK_ORDER_OPERATIONAL_EXCEPTION_EVENT}.cleared`;

/**
 * A Work Queue item on a texting conversation (no work order behind it). Its row carries
 * `item`, which the Work Queue renders like any self-described item.
 */
export const CONVERSATION_OPERATIONAL_EXCEPTION_NOTIFICATION =
  'conversations.operational_exception';

export const CONVERSATION_OPERATIONAL_EXCEPTION_EVENT = `notification.${CONVERSATION_OPERATIONAL_EXCEPTION_NOTIFICATION}`;

export const CONVERSATION_OPERATIONAL_EXCEPTION_CLEARED_EVENT = `${CONVERSATION_OPERATIONAL_EXCEPTION_EVENT}.cleared`;

/**
 * 5.9: Landon's "Move to On its own?" suggestion, an item with no work order behind it. Its row
 * carries `item` like any self-described item, and `data.context.independence_suggestion_id`.
 */
export const INDEPENDENCE_SUGGESTION_NOTIFICATION = 'settings.independence_suggestion';

export const INDEPENDENCE_SUGGESTION_EVENT = `notification.${INDEPENDENCE_SUGGESTION_NOTIFICATION}`;

export const INDEPENDENCE_SUGGESTION_CLEARED_EVENT = `${INDEPENDENCE_SUGGESTION_EVENT}.cleared`;

/**
 * 30c: a recommendation about no job and no texting thread (asking a vendor which job their text is
 * about) gets a company item of its own. Its row carries `item` and `data.context.recommendation_id`.
 */
export const RECOMMENDATION_COMPANY_CARD_NOTIFICATION = 'recommendations.company_card';

export const RECOMMENDATION_COMPANY_CARD_EVENT = `notification.${RECOMMENDATION_COMPANY_CARD_NOTIFICATION}`;

export const RECOMMENDATION_COMPANY_CARD_CLEARED_EVENT = `${RECOMMENDATION_COMPANY_CARD_EVENT}.cleared`;

/**
 * QQ3: Landon's pause on a work order started or ended (Take over, a Decline that handed the job
 * over, Hand back, the job finishing while paused). Sent to every coordinator at the company:
 * `{work_order_id, paused, change, reason}`, ids only.
 */
export const LANDON_PAUSE_CHANGED_EVENT = 'notification.work_orders.landon_pause.changed';

/**
 * QQ4: a Feedback card (Settings › Assistant › Feedback) was made, joined by a decline, applied,
 * dismissed or found stale. Sent to every coordinator at the company: `{feedback_id, change}`.
 */
export const FEEDBACK_CHANGED_EVENT = 'notification.settings.feedback.changed';

/**
 * Kinds whose Clear can't be undone from the UI. The Cleared tab offers no Unclear for them
 * (NotificationButtons, in the Work Queue and the bell panel), and the Work Queue marks their rows
 * `wq-no-undo` (WorkQueue/Listing.tsx) so _tables.scss holds the row's buttons for a moment before
 * they take a pointer click. One list for both, so a new kind can't lose Unclear without also
 * getting the wait.
 */
export const NOTIFICATION_KINDS_WITHOUT_UNCLEAR: ReadonlySet<string> = new Set([
  SERVICE_REQUEST_NEEDS_TRIAGE_ASSISTANCE_NOTIFICATION,
  SERVICE_REQUEST_OPERATIONAL_EXCEPTION_NOTIFICATION,
  WORK_ORDER_OPERATIONAL_EXCEPTION_NOTIFICATION,
  CONVERSATION_OPERATIONAL_EXCEPTION_NOTIFICATION,
  INDEPENDENCE_SUGGESTION_NOTIFICATION,
]);

/**
 * Sent to the users of each vendor whose claim-pending dispatch is recalled or cancelled (ORI-777).
 */
export const VENDOR_REQUEST_DISPATCH_CANCELLED_NOTIFICATION = 'vendor_request.dispatch_cancelled';

export const VENDOR_REQUEST_DISPATCH_CANCELLED_EVENT = `notification.${VENDOR_REQUEST_DISPATCH_CANCELLED_NOTIFICATION}`;

/**
 * Sent to the users of the vendor holding a visit when someone else cancels it (the work order
 * was cancelled, or only the visit was) or moves it to another time.
 */
export const VENDOR_REQUEST_VISIT_CANCELLED_NOTIFICATION = 'vendor_request.visit_cancelled';

export const VENDOR_REQUEST_VISIT_CANCELLED_EVENT = `notification.${VENDOR_REQUEST_VISIT_CANCELLED_NOTIFICATION}`;

export const VENDOR_REQUEST_VISIT_MOVED_NOTIFICATION = 'vendor_request.visit_moved';

export const VENDOR_REQUEST_VISIT_MOVED_EVENT = `notification.${VENDOR_REQUEST_VISIT_MOVED_NOTIFICATION}`;
